import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { signOutRedirect } from "../auth/logout";
import { Dispatch, RootState } from "../store";
import { useExtensionStatus } from "../hooks/useExtensionStatus";
import { EXTENSION_INSTALL_URL } from "../extension";
import { IS_PRODUCTION_CHANNEL } from "../channel";
import NoSeat from "./NoSeat";
import PrepareDemoDialog from "../demo/PrepareDemoDialog";
import { isPreparing } from "../models/demo";
import { apiErrorMessage, impersonate } from "../API";
import { dismissExtensionOffBanner } from "../extensionHandshake";
import { pickDesktopDownload } from "../downloads/desktopLinks";
import ShellView from "../shell/ShellView";
import type { ShellData } from "../shell/ShellData";
import { NAV_ITEMS, navIdFor, requiresSeat, visibleNavItems } from "../shell/navItems";

/** Said instead of downloading on an Intel Mac: the Mac build is arm64 only. */
export const INTEL_MAC_MESSAGE =
  "The Dooglie desktop app needs a Mac with Apple silicon (M1 or later).";

/**
 * The layout route for every signed-in page: builds `ShellData` from the
 * store, the router and the extension check, and draws `ShellView` around the
 * matched page (`<Outlet/>`).
 *
 * Mounted once for the session, so `/get_user_info` and the extension poll run
 * once rather than on every navigation. Seatless users get `NoSeat` instead of
 * the shell on product pages; administration pages open without a seat.
 */
const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<Dispatch>();

  const isOrgAdmin = useSelector((s: RootState) => s.user.is_org_admin);
  const isStaff = useSelector((s: RootState) => s.user.is_dooglie_staff);
  const hasSeat = useSelector((s: RootState) => s.user.has_seat);
  const desktopSeen = useSelector((s: RootState) => s.user.desktop_last_seen_at);
  const onboarding = useSelector((s: RootState) => s.user.onboarding);
  // Prepare demo is for Dooglie staff only (the server says who); nothing of
  // it renders otherwise. The button says when a demo is being prepared or
  // is ready, and opens the dialog either way.
  // Hidden while acting as someone: /demo_session refuses an impersonated call.
  const demoAvailable = useSelector(
    (s: RootState) => s.user.demo_mode_available && s.user.acting_as == null,
  );
  const demoStatus = useSelector((s: RootState) => s.demo.status);
  const [demoOpen, setDemoOpen] = useState(false);
  const actingAs = useSelector((s: RootState) => s.user.acting_as);
  const extensionOff = useSelector(
    (s: RootState) => s.user.extension_switched_off,
  );
  const [stopError, setStopError] = useState<string | null>(null);

  const { status: extension } = useExtensionStatus({
    message: "check_installed",
    field: "installed",
  });

  // Load admin + desktop + onboarding flags once for the whole authed session.
  useEffect(() => {
    dispatch.user.fetchUserInfo();
  }, [dispatch]);

  const hiddenFeatures = useSelector((s: RootState) => s.user.hidden_features);
  const items = useMemo(
    () => visibleNavItems(NAV_ITEMS, { isOrgAdmin, isStaff, hiddenFeatures }),
    [isOrgAdmin, isStaff, hiddenFeatures],
  );

  // Seatless users get no product at all — every gated endpoint would 403 them,
  // so showing the shell would just be a menu of things that fail.
  if (!hasSeat && requiresSeat(location.pathname)) {
    return <NoSeat />;
  }

  const onDownloadDesktop = async () => {
    // The production installers are production Dooglie. Every other channel
    // gets "Dooglie Dev", which installs alongside it and lives on /downloads.
    if (!IS_PRODUCTION_CHANNEL) {
      navigate("/downloads");
      return;
    }
    const pick = await pickDesktopDownload();
    if (!pick) {
      window.alert("Desktop app download is only available for Windows and Mac.");
      return;
    }
    if (pick.intelMac) {
      window.alert(INTEL_MAC_MESSAGE);
      return;
    }
    window.open(pick.url, "_blank");
  };

  const data: ShellData = {
    items,
    active: navIdFor(location.pathname, location.search),
    extension,
    desktop: desktopSeen ? "installed" : "missing",
    // Undefined until /get_user_info answers: no dot until we know.
    onboardingDot: onboarding !== undefined && !onboarding?.completed_at,
    onNavigate: (route) => navigate(route),
    onDownloadExtension: () => window.open(EXTENSION_INSTALL_URL, "_blank"),
    onDownloadDesktop: () => void onDownloadDesktop(),
    demo: demoAvailable
      ? {
          status:
            demoStatus?.status === "ready"
              ? "ready"
              : isPreparing(demoStatus)
                ? "preparing"
                : "idle",
          onOpen: () => setDemoOpen(true),
        }
      : undefined,
    actingAs: actingAs
      ? {
          name: actingAs.name || actingAs.email,
          orgName: actingAs.org_name || null,
          stopError,
          onStop: () => {
            setStopError(null);
            impersonate({ stop: true })
              .then(() => window.location.reload())
              .catch((e) => setStopError(apiErrorMessage(e)));
          },
        }
      : undefined,
    extensionOff: extensionOff
      ? {
          onDismiss: () => {
            dismissExtensionOffBanner();
            dispatch.user.setExtensionSwitchedOff(false);
          },
        }
      : undefined,
    onSignOut: () => {
      void signOutRedirect().catch((err) => console.error("Sign out failed", err));
    },
  };

  return (
    <>
      <ShellView {...data}>
        <Outlet />
      </ShellView>
      {demoAvailable && (
        <PrepareDemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
      )}
    </>
  );
};

export default AppShell;
